import { useEffect, useState } from "react";
import { toast } from "sonner";
import { CheckCircle2, XCircle } from "lucide-react";
import { api, apiError } from "../lib/api";
import { PageHeader } from "./AdminLayout";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";

export default function Settings() {
    const [s, setS] = useState(null);

    useEffect(() => {
        api.get("/admin/settings").then((r) => setS(r.data)).catch((e) => toast.error(apiError(e)));
    }, []);

    const save = async () => {
        try {
            await api.put("/admin/settings", {
                site_name: s.site_name,
                tagline: s.tagline,
                contact_email: s.contact_email,
                socials: s.socials,
                seo: s.seo,
                upload_limits: { video_mb: parseInt(s.upload_limits?.video_mb) || 300, image_mb: parseInt(s.upload_limits?.image_mb) || 10 },
            });
            toast.success("Paramètres enregistrés.");
        } catch (e) {
            toast.error(apiError(e));
        }
    };

    if (!s) return <p className="text-pineneedle">Chargement…</p>;
    const set = (k, v) => setS({ ...s, [k]: v });
    const setSocial = (k, v) => setS({ ...s, socials: { ...s.socials, [k]: v } });
    const setSeo = (k, v) => setS({ ...s, seo: { ...s.seo, [k]: v } });

    return (
        <div data-testid="admin-settings">
            <PageHeader title="Paramètres du site" subtitle="Identité, coordonnées publiques, référencement et limites techniques." />
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 max-w-5xl">
                <div className="rounded-2xl bg-white border border-gold/25 p-6 shadow-sm space-y-4">
                    <h2 className="font-heading font-bold text-forest">Identité & contact</h2>
                    <div><Label>Nom du site</Label><Input value={s.site_name} onChange={(e) => set("site_name", e.target.value)} data-testid="setting-site-name" /></div>
                    <div><Label>Accroche</Label><Input value={s.tagline} onChange={(e) => set("tagline", e.target.value)} data-testid="setting-tagline" /></div>
                    <div><Label>E-mail de contact public</Label><Input type="email" value={s.contact_email} onChange={(e) => set("contact_email", e.target.value)} placeholder="Non configuré" data-testid="setting-contact-email" /></div>
                    {["instagram", "facebook", "tiktok", "youtube"].map((net) => (
                        <div key={net}><Label className="capitalize">{net} (URL complète, laisser vide pour masquer)</Label><Input value={s.socials?.[net] || ""} onChange={(e) => setSocial(net, e.target.value)} data-testid={`setting-social-${net}`} /></div>
                    ))}
                </div>
                <div className="space-y-6">
                    <div className="rounded-2xl bg-white border border-gold/25 p-6 shadow-sm space-y-4">
                        <h2 className="font-heading font-bold text-forest">Référencement (SEO)</h2>
                        <div><Label>Titre SEO</Label><Input value={s.seo?.title || ""} onChange={(e) => setSeo("title", e.target.value)} data-testid="setting-seo-title" /></div>
                        <div><Label>Description SEO</Label><Input value={s.seo?.description || ""} onChange={(e) => setSeo("description", e.target.value)} data-testid="setting-seo-description" /></div>
                    </div>
                    <div className="rounded-2xl bg-white border border-gold/25 p-6 shadow-sm space-y-4">
                        <h2 className="font-heading font-bold text-forest">Limites d'import</h2>
                        <div className="grid grid-cols-2 gap-4">
                            <div><Label>Vidéos (Mo)</Label><Input type="number" value={s.upload_limits?.video_mb} onChange={(e) => setS({ ...s, upload_limits: { ...s.upload_limits, video_mb: e.target.value } })} data-testid="setting-video-limit" /></div>
                            <div><Label>Images (Mo)</Label><Input type="number" value={s.upload_limits?.image_mb} onChange={(e) => setS({ ...s, upload_limits: { ...s.upload_limits, image_mb: e.target.value } })} data-testid="setting-image-limit" /></div>
                        </div>
                    </div>
                    <div className="rounded-2xl bg-white border border-gold/25 p-6 shadow-sm space-y-3">
                        <h2 className="font-heading font-bold text-forest">Intégrations</h2>
                        {Object.entries(s.integrations || {}).map(([k, i]) => (
                            <div key={k} className="flex items-center gap-3 text-sm" data-testid={`integration-${k}`}>
                                {i.configured ? <CheckCircle2 size={16} className="text-green-700" /> : <XCircle size={16} className="text-amber-600" />}
                                <span className="text-pineneedle">{i.label}</span>
                                <span className={`text-xs px-2 py-0.5 rounded-full ml-auto ${i.configured ? "bg-green-100 text-green-800" : "bg-amber-100 text-amber-800"}`}>
                                    {i.configured ? "Configuré" : "Non configuré"}
                                </span>
                            </div>
                        ))}
                    </div>
                </div>
            </div>
            <Button onClick={save} className="mt-6 bg-forest text-cream" data-testid="settings-save">Enregistrer les paramètres</Button>
        </div>
    );
}
